import type {
  FinancialProfile,
  PfmeaRow,
  ProblemRecord,
  RiskLevel,
  SeriesPoint,
  WorkflowStage,
} from "./types";

export const rpn = (sev: number, occ: number, det: number) => sev * occ * det;

export const rpnBefore = (row: PfmeaRow) => rpn(row.sevBefore, row.occBefore, row.detBefore);
export const rpnAfter = (row: PfmeaRow) => rpn(row.sevAfter, row.occAfter, row.detAfter);

export const netPotentialReduction = (exposure: number, actual: number) =>
  Math.max(exposure - actual, 0);

export const delayCost = (npr: number, monthlyCarryFactor: number) => npr * monthlyCarryFactor;

/** Remaining exposure = total exposure − actual cost to date (floored at 0). */
export const remainingExposure = netPotentialReduction;

export const REMAINING_EXPOSURE_TOOLTIP = "Total exposure minus actual cost to date.";

/** Remaining exposure for a record (formerly labelled "Net potential reduction"). */
export const recordNpr = (r: ProblemRecord) =>
  netPotentialReduction(r.financial.exposure, r.financial.actualCost);

export const recordDelayCost = (r: ProblemRecord) =>
  delayCost(recordNpr(r), r.financial.monthlyCarryFactor);

export type EffectivenessVerdict = "Effective" | "Partially Effective" | "Not Effective";

export const effectivenessVerdict = (
  observedPct: number,
  targetPct: number,
): EffectivenessVerdict => {
  if (targetPct <= 0) return "Not Effective";
  const ratio = observedPct / targetPct;
  if (ratio >= 1) return "Effective";
  if (ratio >= 0.6) return "Partially Effective";
  return "Not Effective";
};

export const recordVerdict = (f: FinancialProfile) =>
  effectivenessVerdict(f.observedReductionPct, f.targetReductionPct);

export const claimsPer1000 = (claims: number, unitsInService: number) =>
  unitsInService === 0 ? 0 : (claims / unitsInService) * 1000;

export const riskLevelFromScore = (score: number): RiskLevel => {
  if (score >= 80) return "critical";
  if (score >= 60) return "high";
  if (score >= 40) return "medium";
  return "low";
};

export const stageGateReady = (stage: { requiredEvidence: string[]; evidence: unknown[] }) =>
  stage.evidence.length >= stage.requiredEvidence.length;

/** Required items without real evidence (file, reference or seeded demo evidence). */
export const missingEvidence = (stage: {
  requiredEvidence: string[];
  evidence: { name: string; source?: "file" | "reference"; summary?: string }[];
}) =>
  stage.requiredEvidence.filter(
    (req) =>
      !stage.evidence.some(
        (e) =>
          e.name === req &&
          (e.source === "file" ||
            e.source === "reference" ||
            !(e.summary ?? "").startsWith("Attached in session.")),
      ),
  );

export const workflowProgress = (stages: WorkflowStage[]) => {
  const done = stages.filter((s) => s.status === "complete").length;
  return { done, total: stages.length, pct: Math.round((done / stages.length) * 100) };
};

export const isOverdue = (dueDate: string, status: string, today = new Date()) =>
  status !== "complete" && new Date(dueDate).getTime() < today.getTime();

export const currentStage = (stages: WorkflowStage[]) =>
  stages.find((s) => s.status !== "complete") ?? stages[stages.length - 1]!;

/** I-MR control limits from individual values (moving range, d2 = 1.128). */
export function imrLimits(series: SeriesPoint[]) {
  const values = series.map((p) => p.value);
  const mean = values.reduce((a, b) => a + b, 0) / (values.length || 1);
  const ranges: number[] = [];
  for (let i = 1; i < values.length; i++)
    ranges.push(Math.abs((values[i] ?? 0) - (values[i - 1] ?? 0)));
  const mrBar = ranges.length ? ranges.reduce((a, b) => a + b, 0) / ranges.length : 0;
  const sigma = mrBar / 1.128;
  return {
    mean,
    mrBar,
    ucl: mean + 2.66 * mrBar,
    lcl: Math.max(mean - 2.66 * mrBar, 0),
    mrUcl: 3.267 * mrBar,
    sigma,
    points: series.map((p, i) => ({
      month: p.month,
      value: p.value,
      mr: i === 0 ? null : Math.abs((values[i] ?? 0) - (values[i - 1] ?? 0)),
    })),
  };
}

export function paretoOf(items: { label: string; value: number }[]) {
  const sorted = [...items].sort((a, b) => b.value - a.value);
  const total = sorted.reduce((a, b) => a + b.value, 0) || 1;
  let running = 0;
  return sorted.map((item) => {
    running += item.value;
    return { ...item, cumulative: Math.round((running / total) * 1000) / 10 };
  });
}

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export const fmtCurrency = (n: number) => currency.format(n);

export const fmtCompactCurrency = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

export const fmtNumber = (n: number, digits = 0) =>
  n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export const statusLabel: Record<string, string> = {
  signal: "Signal Review",
  containment: "Containment",
  analysis: "Root Cause",
  implementation: "Implementation",
  verification: "Verification",
  closed: "Closed",
};
